import React, { MouseEvent, PropsWithChildren, useCallback, useState, useMemo, SyntheticEvent } from 'react';

import ModalDialog from '@/components/ModalDialog';
import { ProjectData } from '@/state/types/project-data';
import { Autocomplete, AutocompleteRenderInputParams, IconButton, IconButtonProps, InputAdornment, styled } from '@mui/material';
import { AccessSelector, Container } from '@/components';
import TextField from '@/components/TextField';
import { AccessLevel } from '@bimdata/definitions/const';
import { getUserLabel } from '@/contracts';
import { invite } from '@/actions/project';
import { showMessage } from '@/actions/common';
import BlueButtonWithIcon from '@/components/BlueButtonWithIcon';
import t from '@/utils/translate';
import RolePermissionsInfoDialog from './RolePermissionsInfoDialog';
import hinted from '@/components/Hinted';
import ClearIcon from '@mui/icons-material/Clear';
import isEmailAddressValid from '@/utils/isEmailAddressValid';


const UserSearchBar = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'flex-start',
  justifyContent: 'center',
  margin: '0px',
  gap: '16px',
  padding: '0px',
  ['& div.MuiAutocomplete-root']: {
    width: '292px',
    height: '32px',
    
    ['& label.MuiFormLabel-root']: {
      height: '32px',
      pointerEvents: 'none',
    },

    ['& input.MuiInputBase-input']: {
      width: '100%',
      height: '100%',
    }

  }
}));

const ClearInputButton = styled(IconButton)({
  padding: '4px',
  marginRight: '-2px',
  visibility: 'hidden'
});

const HintedInputAdornment = hinted(
  InputAdornment,
  { placement: 'bottom-end', delay: 250, fadeDuration: 50 }
);

const ClearInputAdornment = (props: IconButtonProps) => (
  <HintedInputAdornment
    hint={t('invitation.action.clear')}
    position="end"
    style={{
      position: 'absolute',
      right: '9px',
      top: '50%',
      transform: 'translate(0, -50%)'
    }}
  >
    <ClearInputButton {...props} className='MuiAutocomplete-clearIndicator'>
      <ClearIcon fontSize='small'/>
    </ClearInputButton>
  </HintedInputAdornment>
);

type InviteParticipantDlgProps = {
  project: ProjectData,
  open: boolean,
  onClose: () => void
};

const StyledDialog = styled(ModalDialog)({
  ['& div.MuiDialogContent-root']: {
    overflowY: 'visible',
    height: '105px',
  }
});

const Info = styled('p')({
  fontSize: '13px',
  fontWeight: 400,
  lineHeight: '15.23px',
  textAlign: 'left',
  margin: '10px 0px 0px 0px',
});

const Note = styled(Info)({
  color: '#6B778C',
});

const HelpLink = styled('span')({
  textDecorationLine: 'underline',
  textDecorationStyle: 'dashed',
  textDecorationColor: '#6B778C',
  cursor: 'pointer',
});

const InlineHelpLink = ({ onHelp, children }: { onHelp: () => void } & PropsWithChildren) => {
  const onClick = useCallback((e: MouseEvent) => {
    onHelp();
    e.stopPropagation();
    e.preventDefault();
  }, [onHelp]);
  return <HelpLink onClick={onClick} >{children}</HelpLink>;
};

export default function InviteParticipantDialog({ project, open, onClose }: InviteParticipantDlgProps) {
  // const [userSearchOptions, setUserSearchOptions] = useState<User[]>([]);
  // const [candidate, setCandidate] = useState<User | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [candidateError, setCandidateError] = useState<string | false>(false);
  const [invitationAccess, setInvitationAccess] = useState<AccessLevel | null>(null);
  const [isInvitationAccessInvalid, setIsInvitationAccessInvalid] = useState<boolean>(true);
  const [invitationLock, setInvitationLock] = useState<boolean>(false);

  const [help, showHelp] = useState(false);

  const onHelpClose = useCallback(() => { showHelp(false); }, []);

  const onHelp = useCallback(() => { showHelp(true); }, []);

  const hint = useMemo(
    () => t(
      'invitation.hint.specify',
      {
        replacers: {
          ['a']: (n: ChildNode) => <InlineHelpLink key={0} onHelp={onHelp}>{n.textContent}</InlineHelpLink>
        }
      },
      true
    ),
    [onHelp]
  );

  const { participants, invitations } = project;

  /*
  const onCandidateChange = useCallback(async (e: Event, v: ) => {
    setCandidate(typeof v === 'string' ? null : v);
  }, []);
  */

  const onSearchQueryChange = useCallback((e: unknown, v: string) => {
    // setCandidate(null);
    setSearchQuery(v);
    setCandidateError(false);
    // if (v?.length >= 3) {
    //  const opts = await search(v).catch(() => []);
    //  setUserSearchOptions(opts);
    // }
  }, []);

  const clearSearchQuery = useCallback(() => onSearchQueryChange(null, ''), []);

  const onInvitationAccessChange = useCallback((level: AccessLevel) => {
    setInvitationAccess(level);
    setIsInvitationAccessInvalid(false);
  }, []);

  const invitationHandler = useCallback(async () => {
    if (invitationLock) return;

    if (!invitationAccess) {
      setIsInvitationAccessInvalid(true);
      return;
    }

    const email = searchQuery.toLowerCase();

    if (!isEmailAddressValid(email)) {
      setCandidateError(t('invitation.validation.email'));
      return;
    }

    if (invitations?.find(({ participant }) => email == (typeof participant == 'string' ? participant : participant.email))) {
      setCandidateError(t('invitation.validation.isInvited'));
      return;
    }

    if (participants?.find(({ user }) => email == user.email)) {
      setCandidateError(t('invitation.validation.isParticipant'));
      return;
    }

    setInvitationLock(true);
    await invite(project.code, invitationAccess!, email);
    showMessage(t('participant.hint.invitationSent'), 'success');
    setSearchQuery('');
    setInvitationAccess(null);
    setIsInvitationAccessInvalid(true);
    setInvitationLock(false);
  }, [invitationLock, searchQuery, invitationAccess]);

  const isInvitationButtonEnabled = !invitationLock && !isInvitationAccessInvalid
    && searchQuery && !candidateError;

  const renderInput = useCallback((params: AutocompleteRenderInputParams) => {
    let endAdornment;
    if (params.InputProps.endAdornment) {
      delete params.InputProps.endAdornment;
      endAdornment = <ClearInputAdornment onClick={clearSearchQuery}/>;
    }

    return <TextField
      {...params}
      slotProps={{
        input: { endAdornment }
      }}
      label={t('invitation.label.email')}
      placeholder={t('invitation.hint.email')}
      size="small"
      {...(
        (searchQuery && candidateError)
        ? {
          error: true,
          helperText: candidateError
        }
        : {}
      )}
    />
  }, [ searchQuery, candidateError ]);

  const closeHandler = useCallback(() => {
    setSearchQuery('');
    setCandidateError(false);
    setInvitationAccess(null);
    setIsInvitationAccessInvalid(true);
    onClose();
  }, [ onClose ]);


  return (
    <StyledDialog
      title={t('participant.title.invite')}
      open={open}
      onClose={closeHandler}
    >
      <div style={{ width: '100%' }}>
        <Info>{hint}</Info>
        <Note>{t('invitation.hint.info1')}</Note>
      </div>
      <UserSearchBar>
        <Autocomplete
          id="candidateField"
          style={{
            width: '373px',
            height: '32px'
          }}
          freeSolo
          getOptionLabel={(o) => typeof o === 'string' ? o : getUserLabel(o)}
          autoComplete
          filterSelectedOptions
          inputValue={searchQuery}
          noOptionsText={t('invitation.hint.userNotFound')}
          onInputChange={onSearchQueryChange}
          options={[]}
          renderInput={renderInput}
        />
        <AccessSelector
          style={{
            width: '155px',
            height: '32px'
          }}
          excludeLevels={[AccessLevel.OWNER]}
          value={invitationAccess}
          onChange={onInvitationAccessChange}
          label={t('invitation.label.access')}
          onHelp={onHelp}
        />
        <BlueButtonWithIcon
          title={t('participant.action.invite')}
          onClick={invitationHandler}
          disabled={!isInvitationButtonEnabled}
          style={{ padding: '8px 12px' }}
        >
          {t('invitation.action.invite')}
        </BlueButtonWithIcon>
      </UserSearchBar>
      <RolePermissionsInfoDialog open={help} onClose={onHelpClose}></RolePermissionsInfoDialog>
    </StyledDialog>
  );
}